<template>
  <div class="iq-gen-status">
    <div
      v-if="showStatusPills"
      class="iq-gen-status__pills"
      role="status"
      :aria-label="`Generation status: ${status}`"
    >
      <span
        v-for="state in states"
        :key="state.key"
        class="iq-gen-status__pill"
        :class="{ 'iq-gen-status__pill--active': status === state.key }"
      >
        {{ state.label }}
      </span>
    </div>

    <div
      class="iq-gen-status__panel"
      :class="{ 'iq-gen-status__panel--stalled': status === 'stalled' }"
    >
      <div class="iq-gen-status__panel-header">
        <span v-if="inProgressStatus" class="iq-gen-status__spinner" aria-hidden="true" />
        <span v-else-if="status === 'done'" class="iq-gen-status__done-icon" aria-hidden="true">
          ✓
        </span>
        <span v-else class="iq-gen-status__stalled-icon" aria-hidden="true">!</span>
        <span class="iq-gen-status__panel-title">Thinking…</span>
        <span v-if="elapsedLabel" class="iq-gen-status__elapsed">{{ elapsedLabel }}</span>
      </div>

      <p v-for="entry in keyedTraceEntries" :key="entry.key" class="iq-gen-status__trace-line">
        {{ entry.text }}
      </p>

      <p v-if="status === 'connecting'" class="iq-gen-status__message">
        Connecting to the generation session…
      </p>
      <p v-else-if="status === 'stalled'" class="iq-gen-status__message">
        Generation stalled. Check your connection and try again.
      </p>

      <div v-if="showActivity" class="iq-gen-status__activity">
        <span class="iq-gen-status__activity-dot" aria-hidden="true" />
        <span>{{ activityLabel }}</span>
      </div>

      <button
        v-if="showRetryButton && status === 'stalled'"
        type="button"
        class="iq-gen-status__retry"
        @click="emit('retry')"
      >
        Retry
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const emit = defineEmits(["retry"]);

const props = defineProps({
  status: {
    type: String,
    default: "connecting",
    validator: (value) => ["connecting", "streaming", "done", "stalled"].includes(value),
  },
  traceEntries: {
    type: Array,
    default: () => [],
  },
  elapsedSeconds: {
    type: Number,
    default: null,
  },
  inProgress: {
    type: Boolean,
    default: true,
  },
  activityLabel: {
    type: String,
    default: "Watching the video…",
  },
  showStatusPills: {
    type: Boolean,
    default: true,
  },
  showRetryButton: {
    type: Boolean,
    default: true,
  },
});

const states = [
  { key: "connecting", label: "Connecting" },
  { key: "streaming", label: "Streaming" },
  { key: "done", label: "Done" },
  { key: "stalled", label: "Stalled" },
];

const inProgressStatus = computed(
  () => props.status === "connecting" || props.status === "streaming"
);
const showActivity = computed(
  () =>
    props.inProgress &&
    inProgressStatus.value &&
    Boolean(String(props.activityLabel ?? "").trim())
);

const elapsedLabel = computed(() => {
  if (props.elapsedSeconds === null) return "";
  return `${props.elapsedSeconds}s`;
});

const keyedTraceEntries = computed(() => {
  const occurrences = new Map();
  return props.traceEntries.map((text) => {
    const occurrence = (occurrences.get(text) || 0) + 1;
    occurrences.set(text, occurrence);
    return { key: `${text}-${occurrence}`, text };
  });
});
</script>

<style scoped>
.iq-gen-status {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.iq-gen-status__pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.iq-gen-status__pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

.iq-gen-status__pill--active {
  border-color: #6941c6;
  background: #f9f5ff;
  color: #6941c6;
  font-weight: 600;
}

.iq-gen-status__panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px;
  border-radius: 12px;
  background: #f4f3ff;
}

.iq-gen-status__panel--stalled {
  border: 1px solid #fecaca;
  background: #fef2f2;
}

.iq-gen-status__panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iq-gen-status__panel-title {
  color: #5d46c9;
  font-size: 14px;
  font-weight: 600;
}

.iq-gen-status__elapsed {
  color: #98a2b0;
  font-size: 13px;
}

.iq-gen-status__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #d9d0fa;
  border-top-color: #6941c6;
  border-radius: 50%;
  animation: iq-gen-status-spin 0.8s linear infinite;
}

.iq-gen-status__done-icon,
.iq-gen-status__stalled-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.iq-gen-status__done-icon {
  background: #dcfae6;
  color: #067647;
}

.iq-gen-status__stalled-icon {
  background: #fee2e2;
  color: #b42318;
}

.iq-gen-status__trace-line,
.iq-gen-status__message {
  margin: 0;
  color: #42307d;
  font-size: 14px;
}

.iq-gen-status__trace-line {
  line-height: 22px;
}

.iq-gen-status__panel--stalled .iq-gen-status__panel-title,
.iq-gen-status__panel--stalled .iq-gen-status__message {
  color: #991b1b;
}

.iq-gen-status__activity {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7a5af8;
  font-size: 13px;
}

.iq-gen-status__activity-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7a5af8;
  animation: iq-gen-status-pulse 1.2s ease-in-out infinite;
}

.iq-gen-status__retry {
  align-self: flex-start;
  padding: 8px 16px;
  border: 0;
  border-radius: 6px;
  background: #dc2626;
  color: #ffffff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.iq-gen-status__retry:hover {
  background: #b91c1c;
}

@keyframes iq-gen-status-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes iq-gen-status-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .iq-gen-status__spinner,
  .iq-gen-status__activity-dot {
    animation: none;
  }
}
</style>
